import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CitationImage, CitationSource, SourcePill, SourceFavicon, hostnameFromUrl, httpsImageSrc } from '@/components/citations'
import { CitationImageView } from '@/components/citations/CitationImageView'
import { sourceImageTokens } from '@/components/citations/citationSource'
import { SocialPostEmbedCard } from '@/pages/KnowledgeHubPage/social/SocialPostEmbedCard'
import { RichInlineText } from './RichInlineText'

import { RichCard } from '@digital-twins/shared-types/assistant-rich-blocks.generated'
import { SocialCitationPost } from '@digital-twins/shared-types'

interface TrendCardsProps {
  isImagePending?: (card: RichCard) => boolean
  cards: Array<RichCard>
  resolveSources: (card: RichCard) => Array<CitationSource>
  /** Photos a card may show, citation order. An asset card passes that asset alone. */
  resolveImages: (card: RichCard) => Array<CitationImage>
  /** The stored social posts a card talks about, shown as post cards under it. */
  resolvePosts?: (card: RichCard) => Array<SocialCitationPost>
}

/** A fixed 4:3 box, centre-cropped, never stretched. */
const IMAGE_BOX_CLASS = 'relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg bg-muted @[30rem]:w-[168px]'

function imageKey(image: CitationImage): string {
  return image.kind === 'url' ? `url:${image.src}` : `social:${image.path}`
}

/** Maximum unique assignment. A missing or broken photo affects only its own card. */
export function assignUniqueCardImages(
  candidates: Array<Array<CitationImage>>,
): Array<CitationImage | null> {
  const owners = new Map<string, number>()
  const assigned: Array<CitationImage | null> = candidates.map(() => null)
  const assign = (card: number, visited: Set<string>): boolean => {
    for (const image of candidates[card]) {
      const key = imageKey(image)
      if (visited.has(key)) continue
      visited.add(key)
      const owner = owners.get(key)
      if (owner === undefined || assign(owner, visited)) {
        owners.set(key, card)
        assigned[card] = image
        return true
      }
    }
    return false
  }
  candidates.forEach((_, card) => assign(card, new Set()))
  return assigned
}

interface TrendCardImageProps {
  image: CitationImage
  alt: string
  testId: string
  onError: () => void
}

/**
 * A card's own image through the shared citation image renderer: a web or
 * library URL loads straight from its host with no referrer, a social post
 * still loads through the authenticated proxy. A failure is reported up, so
 * the block decides what the box shows instead.
 */
/** Decorative loading signal; no promise of an image when selection finishes empty. */
function ImageLoadingGlow() {
  return (
    <div aria-hidden="true" data-testid="thread-card-image-glow" className="absolute inset-0 overflow-hidden bg-background">
      <div className="absolute -left-4 top-1/4 h-24 w-24 rounded-full bg-primary/20 blur-xl motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="absolute -right-4 bottom-1/4 h-28 w-28 rounded-full bg-accent/40 blur-xl motion-safe:animate-pulse motion-reduce:animate-none [animation-delay:700ms]" />
    </div>
  )
}

function TrendCardImage(props: TrendCardImageProps) {
  const { image, alt, testId, onError } = props
  const [loaded, setLoaded] = useState(false)
  const { t } = useTranslation('app')
  const sourceUrl = image.kind === 'url' ? httpsImageSrc(image.sourceUrl) : null
  const imageHost = sourceUrl ? hostnameFromUrl(sourceUrl) : null
  const imageCredit = `${t('threads.detail.imageSource')}${imageHost ? `: ${imageHost}` : ''}`
  /** A picture saved on the message is final: it shows as it arrives, with no glow or fade. */
  const isSaved = image.kind === 'url' && Boolean(sourceImageTokens(image.src))
  const fadeClass = isSaved
    ? ''
    : `transition-opacity duration-300 motion-reduce:transition-none ${loaded ? 'opacity-100' : 'opacity-0'}`

  return (
    <div className={IMAGE_BOX_CLASS} data-testid={testId} aria-busy={!loaded}>
      {!loaded && !isSaved && <ImageLoadingGlow />}
      <CitationImageView
        image={image}
        alt={alt}
        className={`h-full w-full object-center ${fadeClass} ${image.kind === 'url' && image.width && image.height && image.width < image.height ? 'object-contain' : 'object-cover'}`}
        testId={`${testId}-img`}
        onLoad={() => setLoaded(true)}
        onError={onError}
      />
      {loaded && sourceUrl && (
        <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
          aria-label={imageCredit} title={imageCredit}
          className="absolute bottom-1 right-1 inline-flex max-w-full items-center gap-1 rounded bg-background/90 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`${testId}-credit`}>
          <SourceFavicon hostname={imageHost} className="h-3 w-3" />
          <span className="max-w-[16ch] truncate">{imageHost ?? t('threads.detail.imageSource')}</span>
        </a>
      )}
    </div>
  )
}

export function TrendCards(props: TrendCardsProps) {
  const { cards, resolveSources, resolveImages, resolvePosts, isImagePending } = props

  const [failedKeys, setFailedKeys] = useState<Array<string>>([])

  const images = assignUniqueCardImages(
    cards.map((card) =>
      resolveImages(card).filter((image) => !failedKeys.includes(imageKey(image))),
    ),
  )

  const onImageError = (key: string) => {
    setFailedKeys((previous) => (previous.includes(key) ? previous : [...previous, key]))
  }

  return (
    <div className="@container my-7" data-testid="thread-rich-cards">
      {cards.map((card, index) => {
        const sources = resolveSources(card)
        const image = images[index]
        let imageBox = null
        if (image) {
          const key = imageKey(image)
          imageBox = (
            <TrendCardImage
              key={key}
              image={image}
              alt={card.title}
              testId={`thread-rich-card-${index}-image`}
              onError={() => onImageError(key)}
            />
          )
        }
        if (!image && isImagePending?.(card)) {
          imageBox = (
            <div className={IMAGE_BOX_CLASS} aria-busy="true" data-testid={`thread-rich-card-${index}-image-pending`}>
              <ImageLoadingGlow />
            </div>
          )
        }
        const pill = sources.length > 0 ? (
          <div className="mt-2">
            <SourcePill sources={sources} testId={`thread-citation-marker-card-${index}`} />
          </div>
        ) : null

        const posts = resolvePosts ? resolvePosts(card) : []
        const postCards = posts.length > 0 ? (
          <div className="mt-3 flex flex-col gap-2" data-testid={`thread-rich-card-${index}-posts`}>
            {posts.map((post, postIndex) => (
              <SocialPostEmbedCard
                key={post.postToken}
                post={post}
                testId={`thread-rich-card-${index}-post-${postIndex}`}
              />
            ))}
          </div>
        ) : null

        return (
          <div
            key={index}
            className="flex flex-col gap-3 border-t border-border/60 py-5 last:border-b @[30rem]:flex-row @[30rem]:gap-5"
            data-testid={`thread-rich-card-${index}`}
          >
            {imageBox}
            <div className="@container min-w-0 flex-1">
              <div className="mb-1 text-[13px] font-semibold text-muted-foreground tabular-nums">{index + 1}</div>
              <div className="text-[17px] font-semibold leading-[1.35] text-foreground">
                <RichInlineText text={card.title} />
              </div>
              <div className="mt-1.5 text-base leading-[1.7] text-foreground" data-testid={`thread-rich-card-${index}-body`}>
                <RichInlineText text={card.body} />
              </div>
              {pill}
              {postCards}
            </div>
          </div>
        )
      })}
    </div>
  )
}
